import { 
    createSlice,
    PayloadAction,
    isAnyOf,
    createSelector
} from "@reduxjs/toolkit";
import { createRecommendationsThunk, getRecommendationsThunk, updateRecommendationsThunk } from "./Thunk";
import { RecommendationAPIResponse } from '../../../api/Recommendations'
import { RootState } from "../../../app/store";

interface RecommendationEntry {
    recommendation: RecommendationAPIResponse | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null | undefined;
}

interface RecommendationsState {
    [caseId: string]: RecommendationEntry;
}

const initialState: RecommendationsState = {};

const recommendationsSlice = createSlice({
    name: 'recommendations',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(updateRecommendationsThunk.fulfilled, (state, action) => {
                const caseId = action.meta.arg.caseId;
                if (!caseId || !state[caseId]) return;
                
                state[caseId].status = 'succeeded';
                const { request } = action.meta.arg;
                if (state[caseId].recommendation) {
                    state[caseId].recommendation = {
                        ...state[caseId].recommendation,
                        ...request
                    };
                }
            })
            .addMatcher(
                isAnyOf(createRecommendationsThunk.fulfilled, getRecommendationsThunk.fulfilled),
                (state, action: PayloadAction<RecommendationAPIResponse>) => {
                    const caseId = action.payload.case_id;
                    if (!caseId) return;

                    if (!state[caseId]) {
                        state[caseId] = {
                            recommendation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'succeeded';
                    state[caseId].recommendation = action.payload;
                }
            )
            .addMatcher(
                isAnyOf(createRecommendationsThunk.pending, getRecommendationsThunk.pending),
                (state, action) => {
                    let caseId: string | undefined;
                    
                    if ('case_id' in action.meta.arg) {
                        caseId = action.meta.arg.case_id;
                    } else if ('caseId' in action.meta.arg) {
                        caseId = action.meta.arg.caseId;
                    }
                    
                    if (!caseId) return;

                    if (!state[caseId]) {
                        state[caseId] = {
                            recommendation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'loading';
                    state[caseId].error = null;
                }
            )
            .addMatcher(
                isAnyOf(createRecommendationsThunk.rejected, getRecommendationsThunk.rejected, updateRecommendationsThunk.rejected),
                (state, action) => {
                    let caseId: string | undefined;
                    
                    if ('case_id' in action.meta.arg) {
                        caseId = action.meta.arg.case_id;
                    } else if ('caseId' in action.meta.arg) {
                        caseId = action.meta.arg.caseId;
                    }
                    
                    if (!caseId) return;

                    if (!state[caseId]) {
                        state[caseId] = {
                            recommendation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'failed';
                    state[caseId].error = action.payload ?? action.error.message ?? 'Unknown fetch error';
                }
            );
    },
});

export const { resetRecommendations, resetRecommendationForCase } = recommendationsSlice.actions;
export const recommendationsReducer = recommendationsSlice.reducer;

const selectRecommendationEntry = (
    state: RootState,
    caseId: string | undefined
) => {
    return caseId ? state.recommendationsData[caseId] : undefined;
};

export const selectCurrentRecommendation = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectRecommendationEntry(state, caseId);
        return entry?.recommendation ?? null;
    }
);

export const selectRecommendationStatus = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectRecommendationEntry(state, caseId);
        return entry?.status ?? 'idle';
    }
);

export const selectRecommendationsError = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectRecommendationEntry(state, caseId);
        return entry?.error ?? null;
    }
);